<template>
  <div class="RendererXlsx">
    <div class="RendererXlsx__body">
      <UISpinner v-if="loading" size="md" class="RendererXlsx__loader" />
      <div v-else-if="errorMessage" class="RendererXlsx__error">
        {{ errorMessage }}
      </div>
      <template v-else>
        <p v-if="hint" class="RendererXlsx__hint">{{ hint }}</p>
        <div v-if="rows.length === 0" class="RendererXlsx__empty">
          No rows to display (empty sheet or unreadable format).
        </div>
        <div v-else class="RendererXlsx__scroll">
          <table class="RendererXlsx__table">
            <tbody>
              <tr v-for="(row, ri) in rows" :key="ri">
                <td v-for="(cell, ci) in row" :key="ci">
                  {{ cell }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { UISpinner } from '@foundever/ui-lib'

interface Props {
  content?: string
  title: string
  rows: string[][]
  loading?: boolean
  errorMessage?: string | null
  truncatedRows?: boolean
  truncatedCols?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  content: '',
  loading: false,
  errorMessage: null,
  truncatedRows: false,
  truncatedCols: false,
})

const hint = computed(() => {
  const parts: string[] = []
  if (props.truncatedRows) {
    parts.push('Showing the first 200 rows')
  }
  if (props.truncatedCols) {
    parts.push('Showing the first 40 columns')
  }
  if (!parts.length) return ''
  return `${parts.join(' · ')}. Download the file to see the full sheet.`
})
</script>

<style scoped lang="scss">
.RendererXlsx {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 0.75rem;
  padding: 0 0.25rem;

  .RendererXlsx__body {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .RendererXlsx__loader {
    margin: auto;
  }

  .RendererXlsx__error {
    @include font-size-14px;
    color: var(--text-status-error);
    padding: 1rem 0;
  }

  .RendererXlsx__hint {
    @include font-size-12px;
    color: var(--text-tertiary);
    margin: 0;
    flex-shrink: 0;
  }

  .RendererXlsx__empty {
    @include font-size-14px;
    color: var(--text-tertiary);
    padding: 1rem 0;
  }

  .RendererXlsx__scroll {
    @include customScrollbarInset(0.75rem);
    background: var(--bg-primary);
    flex: 1;
    min-height: 0;
  }

  .RendererXlsx__table {
    @include font-size-12px;
    border-collapse: collapse;
    width: max-content;
    min-width: 100%;

    td {
      border: 1px solid var(--border-default);
      padding: 0.35rem 0.5rem;
      max-width: 16rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      vertical-align: top;
    }

    tr:nth-child(even) td {
      background-color: var(--bg-secondary);
    }
  }
}
</style>
